/*
 * Copyright (c) 2022 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { UseMaterialColors, UseUserRoleNotes } from "lib/hooks";
import { SectionStyle } from "lib/styles/shared";
import { GetSharedTableStyles, TableContainerStyles } from "lib/styles/table";
import React, { useCallback, useMemo, useRef } from "react";

type plantNoteRow = { id: string; disabled: boolean };

export function EmailNotificationSettings() {
	const materialColors = UseMaterialColors();
	const { sectorsWithPlants, loading, updateNotificationSetting } =
		UseUserRoleNotes();

	const handleToggleChange = useCallback(
		(userRoleId: string, noteId: string) =>
			(event: React.ChangeEvent<HTMLInputElement>) => {
				updateNotificationSetting(
					userRoleId,
					noteId,
					event.target.checked
				);
			},
		[updateNotificationSetting]
	);

	type noteColumn = {
		noteId: string;
		noteName: string;
		noteSequence: number;
	};
	const prevNoteKeyRef = useRef("");
	const cachedColumnsRef = useRef<noteColumn[]>([]);

	const allNotes = useMemo(() => {
		const noteMap = new Map<string, noteColumn>();
		const sectorIds = new Map<string, Set<string>>();

		for (const sector of sectorsWithPlants) {
			const ids = new Set<string>();
			for (const plant of sector.plants) {
				for (const note of plant.notes || []) {
					if (!note?.id || !note?.name) continue;
					ids.add(note.id);
					if (!noteMap.has(note.id)) {
						noteMap.set(note.id, {
							noteId: note.id,
							noteName: note.name,
							noteSequence:
								note.sequence ?? Number.MAX_SAFE_INTEGER,
						});
					}
				}
			}
			sectorIds.set(sector.id, ids);
		}

		const key = [...noteMap.keys()].sort().join("\0");
		if (key !== prevNoteKeyRef.current) {
			prevNoteKeyRef.current = key;
			cachedColumnsRef.current = [...noteMap.values()].sort(
				(a, b) => a.noteSequence - b.noteSequence
			);
		}

		return { columns: cachedColumnsRef.current, sectorNoteIds: sectorIds };
	}, [sectorsWithPlants]);

	const sharedTableStyles = GetSharedTableStyles("small");
	const tableContainerSx = TableContainerStyles();

	return (
		<Box
			sx={{
				p: 2,
				width: "100%",
				height: "100%",
			}}
		>
			<Box sx={SectionStyle}>
				<Typography variant="h4" gutterBottom>
					Email Notification Settings
				</Typography>
				<Typography
					variant="body2"
					sx={{
						color: materialColors.onSurfaceVariant,
						mt: 1,
					}}
				>
					Manage email notification preferences for containment events
					by plant.{" "}
				</Typography>
			</Box>

			{!loading && sectorsWithPlants.length > 0 && (
				<Stack spacing={3}>
					{sectorsWithPlants.map((sector) => (
						<Accordion
							key={sector.id}
							defaultExpanded
							sx={{
								"backgroundColor": materialColors.surface[1],
								"borderRadius": 2,
								"boxShadow": materialColors.isDark
									? "0 2px 8px rgba(0,0,0,0.2)"
									: "0 2px 8px rgba(0,0,0,0.08)",
								"&:before": {
									display: "none",
								},
								"margin": "3 !important",
								"&.Mui-expanded": {
									margin: "3 !important",
								},
							}}
						>
							<AccordionSummary
								expandIcon={
									<ExpandMoreIcon
										sx={{
											color: materialColors.primary,
										}}
									/>
								}
								sx={{
									"px": 3,
									"py": 2,
									"&.Mui-expanded": {
										minHeight: 56,
									},
									"& .MuiAccordionSummary-content": {
										"margin": "12px 0",
										"&.Mui-expanded": {
											margin: "12px 0",
										},
									},
								}}
							>
								<Typography
									variant="h5"
									sx={{
										fontWeight: 600,
										color: materialColors.onSurface,
									}}
								>
									{sector.name}
								</Typography>
							</AccordionSummary>
							<AccordionDetails sx={{ p: 0, pb: 1 }}>
								<Box sx={{ px: 3, py: 2 }}>
									<TableContainer sx={tableContainerSx}>
										<Table
											size="small"
											stickyHeader
											sx={{
												...sharedTableStyles.root,
												tableLayout: "fixed",
											}}
										>
											<TableHead>
												<TableRow>
													<TableCell>Plant</TableCell>
													{allNotes.columns.map(
														(c) => {
															const present =
																allNotes.sectorNoteIds
																	.get(
																		sector.id
																	)
																	?.has(
																		c.noteId
																	);
															return present ? (
																<TableCell
																	key={
																		c.noteId
																	}
																	align="center"
																>
																	{c.noteName}
																</TableCell>
															) : (
																<TableCell
																	key={
																		c.noteId
																	}
																	aria-hidden="true"
																	role="presentation"
																/>
															);
														}
													)}
													<TableCell
														sx={{
															width: "30%",
															px: 0,
														}}
													/>
												</TableRow>
											</TableHead>
											<TableBody>
												{sector.plants.map((plant) => {
													const noteById = new Map<
														string,
														plantNoteRow
													>();
													(plant.notes || []).forEach(
														(n: any) => {
															if (n?.id) {
																noteById.set(
																	String(
																		n.id
																	),
																	{
																		id: String(
																			n.id
																		),
																		disabled:
																			!!n.disabled,
																	}
																);
															}
														}
													);

													return (
														<TableRow
															key={
																plant.userRoleId
															}
															hover
														>
															<TableCell>
																{plant.name}
															</TableCell>
															{allNotes.columns.map(
																(col) => {
																	const sectorHasNote =
																		allNotes.sectorNoteIds
																			.get(
																				sector.id
																			)
																			?.has(
																				col.noteId
																			);

																	if (
																		!sectorHasNote
																	) {
																		return (
																			<TableCell
																				key={
																					col.noteId
																				}
																				aria-hidden="true"
																				role="presentation"
																			/>
																		);
																	}

																	const note =
																		noteById.get(
																			col.noteId
																		);
																	return (
																		<TableCell
																			key={
																				col.noteId
																			}
																			align="center"
																		>
																			<Switch
																				checked={
																					note
																						? !note.disabled
																						: false
																				}
																				disabled={
																					!note
																				}
																				onChange={handleToggleChange(
																					plant.userRoleId,
																					col.noteId
																				)}
																				color="primary"
																				size="medium"
																			/>
																		</TableCell>
																	);
																}
															)}
															<TableCell
																sx={{
																	width: "30%",
																	px: 0,
																}}
															/>
														</TableRow>
													);
												})}
											</TableBody>
										</Table>
									</TableContainer>
								</Box>
							</AccordionDetails>
						</Accordion>
					))}
				</Stack>
			)}
		</Box>
	);
}
